/*
 * The immigration timeline above the records table: lanes for status,
 * physical presence, I-94 and passport, summary tiles and check-this flags.
 * Everything works in UTC calendar days so a date never shifts by timezone.
 * Flags prompt the attorney to look at something; they never state a
 * legal conclusion.
 */
import type {
  ForeignNationalImmigrationEntryExitHistoryEntry as TravelEntry,
  ForeignNationalImmigrationStatusHistoryEntry as StatusEntry
} from "../../../../api/types/foreignNationalImmigrationHistory";
import type { ForeignNationalPassport } from "../../../../api/types/foreignNationalRecords";

const DAY_MS = 86_400_000;
const NEAR_DAYS = 180;

export type SegmentKind = "status" | "none" | "in" | "out" | "i94" | "doc";

export type Segment = {
  /** UTC day numbers; `to` is exclusive. */
  from: number;
  to: number;
  label: string;
  kind: SegmentKind;
  /** Ended before today (drawn muted). */
  past?: boolean;
  /** Runs on past the last known date (no end recorded). */
  open?: boolean;
};

export type Lanes = {
  status: Segment[];
  presence: Segment[];
  i94: Segment[];
  passport: Segment[];
};

export type Tile = {
  label: string;
  value: string;
  sub?: string;
  tone?: "red" | "amber";
};

export type Flag = { title: string; detail: string };

export type TimelineView = {
  lanes: Lanes;
  window: { from: number; to: number };
  today: number;
  tiles: Tile[];
  flags: Flag[];
};

export const dayOf = (iso: string | null | undefined): number | null => {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso ?? "");
  if (!m) return null;
  return Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3])) / DAY_MS;
};

export const todayDay = (now: Date = new Date()): number =>
  Date.UTC(now.getFullYear(), now.getMonth(), now.getDate()) / DAY_MS;

export const formatDay = (day: number): string =>
  new Date(day * DAY_MS).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    timeZone: "UTC"
  });

export const yearOf = (day: number): number =>
  new Date(day * DAY_MS).getUTCFullYear();

export const yearStart = (year: number): number =>
  Date.UTC(year, 0, 1) / DAY_MS;

/** "3 yrs 4 mo", "5 mo", "12 days". */
export const formatSpan = (days: number): string => {
  if (days < 31) return `${days} day${days === 1 ? "" : "s"}`;
  const months = Math.floor(days / 30.44);
  const years = Math.floor(months / 12);
  const rest = months % 12;
  if (years === 0) return `${months} mo`;
  const y = `${years} yr${years === 1 ? "" : "s"}`;
  return rest ? `${y} ${rest} mo` : y;
};

const text = (v: string | null | undefined) => (v ?? "").trim();

const isJ1 = (status: string) => /^J-?1\b/i.test(status.replace(/\s/g, ""));

type Interval = [number, number];

/** `base` minus every interval in `cuts`. */
const subtract = (base: Interval[], cuts: Interval[]): Interval[] =>
  cuts.reduce<Interval[]>(
    (acc, [cf, ct]) =>
      acc.flatMap(([f, t]): Interval[] => {
        if (ct <= f || cf >= t) return [[f, t]];
        const out: Interval[] = [];
        if (cf > f) out.push([f, cf]);
        if (ct < t) out.push([ct, t]);
        return out;
      }),
    base
  );

const statusSegments = (statuses: StatusEntry[], today: number): Segment[] => {
  const rows = statuses
    .map((s) => ({ s, from: dayOf(s.validity_start_date) }))
    .filter((r): r is { s: StatusEntry; from: number } => r.from !== null)
    .sort((a, b) => a.from - b.from);
  return rows.map(({ s, from }, i) => {
    const end = dayOf(s.validity_expiration_date);
    const next = rows[i + 1]?.from ?? null;
    const open = end === null;
    let to: number;
    if (end !== null) to = end + 1;
    else if (s.is_current) to = today + 1;
    else to = next ?? today;
    const label = [text(s.status) || "Status", text(s.employer)]
      .filter(Boolean)
      .join(" · ");
    return {
      from,
      to: Math.max(to, from + 1),
      label: s.duration_of_status ? `${label} · D/S` : label,
      kind: "status",
      past: to <= today,
      open: open && !s.is_current && next === null
    };
  });
};

const travelRows = (travel: TravelEntry[]) =>
  travel
    .map((t) => ({ t, day: dayOf(t.event_date) }))
    .filter((r): r is { t: TravelEntry; day: number } => r.day !== null)
    .sort((a, b) => a.day - b.day);

const presenceSegments = (
  travel: TravelEntry[],
  firstStatusDay: number | null,
  today: number
): Segment[] => {
  const rows = travelRows(travel);
  const out: Segment[] = [];
  if (rows.length === 0) return out;
  if (rows[0].t.event_type === "EXIT" && firstStatusDay !== null) {
    if (firstStatusDay < rows[0].day) {
      out.push({
        from: firstStatusDay,
        to: rows[0].day,
        label: "In the U.S.",
        kind: "in",
        past: true
      });
    }
  }
  rows.forEach(({ t, day }, i) => {
    const to = rows[i + 1]?.day ?? today;
    if (to <= day) return;
    const inside = t.event_type === "ENTRY";
    out.push({
      from: day,
      to,
      label: inside
        ? "In the U.S."
        : `Outside the U.S. · ${formatSpan(to - day)}`,
      kind: inside ? "in" : "out",
      past: to < today
    });
  });
  return out;
};

const i94Segments = (travel: TravelEntry[], today: number): Segment[] => {
  const rows = travelRows(travel);
  return rows
    .map(({ t, day }, i): Segment | null => {
      if (t.event_type !== "ENTRY") return null;
      const number = text(t.i94_record_number);
      const cls = text(t.class_of_admission);
      if (!number && !cls && !t.i94_expiration_date) return null;
      const exit = rows.slice(i + 1).find((r) => r.t.event_type === "EXIT");
      const end = dayOf(t.i94_expiration_date);
      const to = end !== null ? end + 1 : exit?.day ?? today;
      const label =
        end === null
          ? [cls, "D/S or no end date"].filter(Boolean).join(" · ")
          : number || cls || "I-94";
      return {
        from: day,
        to: Math.max(to, day + 1),
        label,
        kind: "i94",
        past: to <= today
      };
    })
    .filter((s): s is Segment => s !== null);
};

const passportSegments = (
  passports: ForeignNationalPassport[],
  today: number
): Segment[] =>
  passports
    .map((p): Segment | null => {
      const to = dayOf(p.expiration_date);
      const issued = dayOf(p.issue_date);
      if (to === null && issued === null) return null;
      const from = issued ?? (to as number) - 3652;
      const end = to !== null ? to + 1 : today;
      const label = [
        text(p.passport_number),
        text(p.issuing_country),
        to !== null
          ? `${to < today ? "expired" : "valid to"} ${formatDay(to)}`
          : null
      ]
        .filter(Boolean)
        .join(" · ");
      return {
        from,
        to: Math.max(end, from + 1),
        label: label || "Passport",
        kind: "doc",
        past: end <= today
      };
    })
    .filter((s): s is Segment => s !== null)
    .sort((a, b) => a.from - b.from);

/**
 * Days with no status on file while the client was (or may have been) in the
 * U.S. With no travel recorded, presence is assumed from the first status on.
 */
const noneSegments = (
  status: Segment[],
  presence: Segment[],
  today: number
): Segment[] => {
  if (status.length === 0) return [];
  const inside: Interval[] =
    presence.length > 0
      ? presence
          .filter((p) => p.kind === "in")
          .map((p): Interval => [p.from, p.to])
      : [[status[0].from, today]];
  const covered = status.map((s): Interval => [s.from, s.to]);
  return subtract(inside, covered)
    .filter(([f, t]) => t - f >= 1 && f < today)
    .map(([f, t]) => ({
      from: f,
      to: Math.min(t, today),
      label: "None",
      kind: "none" as const,
      past: t < today
    }));
};

const currentTile = (status: Segment[], today: number): Tile => {
  const now = status.find((s) => s.from <= today && s.to > today);
  if (now) {
    const left = now.to - today;
    return {
      label: "Current status",
      value: now.label.split(" · ")[0],
      sub: now.label.endsWith("D/S")
        ? "Duration of status"
        : `Until ${formatDay(now.to - 1)} · ${formatSpan(left)} left`,
      tone: left <= 90 && !now.label.endsWith("D/S") ? "amber" : undefined
    };
  }
  const last = [...status].reverse().find((s) => s.to <= today);
  if (last) {
    return {
      label: "Current status",
      value: "None on file",
      sub: `${last.label.split(" · ")[0]} ended ${formatDay(last.to - 1)}`,
      tone: "red"
    };
  }
  return {
    label: "Current status",
    value: status.length ? "Starts later" : "None on file",
    sub: status.length ? formatDay(status[0].from) : "Add a status below"
  };
};

const travelTiles = (travel: TravelEntry[], today: number): Tile[] => {
  const rows = travelRows(travel).filter((r) => r.day <= today);
  const lastEntry = [...rows].reverse().find((r) => r.t.event_type === "ENTRY");
  const last = rows[rows.length - 1];
  const entryTile: Tile = lastEntry
    ? {
        label: "Last entry",
        value: formatDay(lastEntry.day),
        sub:
          [
            text(lastEntry.t.port_of_entry),
            text(lastEntry.t.class_of_admission)
              ? `admitted as ${text(lastEntry.t.class_of_admission)}`
              : ""
          ]
            .filter(Boolean)
            .join(" · ") || undefined
      }
    : {
        label: "Last entry",
        value: "Not recorded",
        sub: "Add an entry record"
      };
  let stayTile: Tile;
  if (!last) {
    stayTile = { label: "In the U.S. since last entry", value: "—" };
  } else if (last.t.event_type === "ENTRY") {
    stayTile = {
      label: "In the U.S. since last entry",
      value: formatSpan(today - last.day),
      sub: "No trips recorded since"
    };
  } else {
    stayTile = {
      label: "Outside the U.S.",
      value: `Since ${formatDay(last.day)}`,
      sub: formatSpan(today - last.day)
    };
  }
  return [entryTile, stayTile];
};

const flagsFor = (
  statuses: StatusEntry[],
  lanes: Lanes,
  today: number
): Flag[] => {
  const flags: Flag[] = [];
  statuses
    .filter((s) => isJ1(text(s.status)))
    .forEach((s) => {
      const from = dayOf(s.validity_start_date);
      const to = dayOf(s.validity_expiration_date);
      flags.push({
        title: `J-1${
          from !== null
            ? ` from ${yearOf(from)}${to !== null ? ` to ${yearOf(to)}` : ""}`
            : ""
        }`,
        detail: "See if the two-year home residency rule applies"
      });
    });
  const gaps = lanes.status.filter(
    (s) => s.kind === "none" && s.to - s.from >= 1
  );
  const pastGaps = gaps.filter((g) => g.to < today);
  if (pastGaps.length > 0) {
    const days = pastGaps.reduce((n, g) => n + (g.to - g.from), 0);
    flags.push({
      title: `${formatSpan(days)} with no status on file`,
      detail:
        "Check for a pending extension or change of status, or a trip that isn't recorded"
    });
  }
  lanes.i94.forEach((i94) => {
    const status = lanes.status.find(
      (s) =>
        s.kind === "status" &&
        s.from <= i94.from + 30 &&
        s.to > i94.from &&
        !s.label.endsWith("D/S")
    );
    if (status && i94.to < status.to - 1 && i94.to > today - 365) {
      flags.push({
        title: `I-94 ends ${formatDay(i94.to - 1)}, before the ${
          status.label.split(" · ")[0]
        } approval`,
        detail: "Check which date controls the authorized stay"
      });
    }
  });
  const passport = [...lanes.passport]
    .reverse()
    .find((p) => p.label.includes("valid to") || p.label.includes("expired"));
  if (passport && passport.to - today <= NEAR_DAYS) {
    flags.push({
      title:
        passport.to <= today
          ? `Passport expired ${formatDay(passport.to - 1)}`
          : `Passport expires ${formatDay(passport.to - 1)}`,
      detail: "A renewal may be needed before travel or filing"
    });
  }
  return flags;
};

export const buildTimelineView = (
  statuses: StatusEntry[],
  travel: TravelEntry[],
  passports: ForeignNationalPassport[],
  now: Date = new Date()
): TimelineView => {
  const today = todayDay(now);
  const status = statusSegments(statuses, today);
  const presence = presenceSegments(travel, status[0]?.from ?? null, today);
  const none = noneSegments(status, presence, today);
  const lanes: Lanes = {
    status: [...status, ...none].sort((a, b) => a.from - b.from),
    presence,
    i94: i94Segments(travel, today),
    passport: passportSegments(passports, today)
  };
  const all = [
    ...lanes.status,
    ...lanes.presence,
    ...lanes.i94,
    ...lanes.passport
  ];
  const first = all.length
    ? Math.min(...all.map((s) => s.from))
    : today - 3 * 365;
  const last = Math.max(today + 365, ...all.map((s) => s.to));
  const window = {
    from: yearStart(yearOf(first)),
    to: Math.min(yearStart(yearOf(last) + 1), yearStart(yearOf(today) + 4))
  };
  const flags = flagsFor(statuses, lanes, today);
  const flagTile: Tile = flags.length
    ? {
        label: "Worth checking",
        value: flags[0].title,
        sub:
          flags.length > 1
            ? `${flags[0].detail} · ${flags.length - 1} more`
            : flags[0].detail,
        tone: "amber"
      }
    : {
        label: "Worth checking",
        value: "Nothing flagged",
        sub: "Based on the records below"
      };
  return {
    lanes,
    window,
    today,
    tiles: [
      currentTile(status, today),
      ...travelTiles(travel, today),
      flagTile
    ],
    flags
  };
};

/** Year ticks for the axis, thinned to stay readable. */
export const yearTicks = (window: { from: number; to: number }): number[] => {
  const first = yearOf(window.from);
  const last = yearOf(window.to - 1);
  const span = last - first + 1;
  let step = 1;
  if (span > 24) step = 5;
  else if (span > 14) step = 2;
  const out: number[] = [];
  for (let y = first; y <= last; y += step) out.push(y);
  return out;
};
